import { createApi } from "@reduxjs/toolkit/query/react";
import { ApiEndpoints, baseQueryWithAuth } from "../../core";
import { PlansResponse, AddScreensResponse } from "../../types"; // Adjust types as needed
import { toast } from "../../core/toast";

export const planApi = createApi({
  reducerPath: "planApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Plan"],
  endpoints: (builder) => ({
    getPlans: builder.query<
      PlansResponse,
      { name?: string; type?: string; page?: number; limit?: number } | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params && typeof params === "object") {
          if (params.name) queryParams.append("name", params.name);
          if (params.type) queryParams.append("type", params.type);
          if (params.page) queryParams.append("page", params.page.toString());
          if (params.limit)
            queryParams.append("limit", params.limit.toString());
        }
        const queryString = queryParams.toString();
        return {
          url: `${ApiEndpoints.getPlans}${queryString ? `?${queryString}` : ""}`,
          method: "GET",
        };
      },
      providesTags: ["Plan"],
    }),
    getPlanById: builder.query<any, string>({
      query: (id) => ({
        url: `${ApiEndpoints.getPlanById}/${id}`, // Assuming this is the correct endpoint pattern
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "Plan", id }],
    }),
    createPlan: builder.mutation<any, any>({
      query: (body) => ({
        url: ApiEndpoints.createPlan, // Assuming POST to /plans
        method: "POST",
        body,
      }),
      invalidatesTags: ["Plan"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message || "Plan created successfully");
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error creating plan");
        }
      },
    }),
    editPlan: builder.mutation<any, { id: string; body: any }>({
      query: ({ id, body }) => ({
        url: `${ApiEndpoints.editPlan}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Plan", id },
        "Plan",
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message || "Plan updated successfully");
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error updating plan");
        }
      },
    }),
    deletePlan: builder.mutation<any, string>({
      query: (id) => ({
        url: `${ApiEndpoints.deletePlan}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Plan"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message || "Plan deleted successfully");
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error deleting plan");
        }
      },
    }),
  }),
});

export const {
  useGetPlansQuery,
  useGetPlanByIdQuery,
  useCreatePlanMutation,
  useEditPlanMutation,
  useDeletePlanMutation,
} = planApi;
